import React, { useState, useEffect } from 'react';
import { Search, Calendar, Clock, MapPin, Users, Filter, CheckCircle } from 'lucide-react';
import { useSchedule } from '../contexts/ScheduleContext';
import { eventsService } from '../services/eventsService';

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDay, setSelectedDay] = useState('all');
  const [feedback, setFeedback] = useState({ show: false, message: '', type: '' });

  const { addToSchedule, isEventScheduled } = useSchedule();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const eventsData = await eventsService.getAllEvents();
      const eventsArray = eventsData.data || eventsData;
      setEvents(eventsArray);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'keynote', label: 'Keynote' },
    { value: 'solo_talk', label: 'Solo Talk' },
    { value: 'fireside_chat', label: 'Fireside Chat' },
    { value: 'panel', label: 'Panel' },
    { value: 'engagement_event', label: 'Engagement Event' }
  ];

  const days = [
    { value: 'all', label: 'All Days' },
    { value: 'day1', label: 'Day 1 - Feb 21 (Saturday)' },
    { value: 'day2', label: 'Day 2 - Feb 22 (Sunday)' }
  ];

  // Normalize time to minutes for chronological sorting
  const normalizeTime = (timeStr) => {
    if (!timeStr) return 0;

    // Prefer AM/PM formats
    const directMatch = timeStr.match(/(\d{1,2}:\d{2})\s*([AP]M)/i);
    if (directMatch) {
      const [timePart, modifier] = [directMatch[1], directMatch[2]];
      let [h, m] = timePart.split(':').map(Number);
      const mod = modifier.toLowerCase();
      if (mod === 'pm' && h !== 12) h += 12;
      if (mod === 'am' && h === 12) h = 0;
      return h * 60 + (m || 0);
    }

    // Handle ranges like "1:00 - 2:00 PM" where AM/PM appears only once
    const rangeMatch = timeStr.match(/(\d{1,2}:\d{2}).*([AP]M)/i);
    if (rangeMatch) {
      const [timePart, modifier] = [rangeMatch[1], rangeMatch[2]];
      let [h, m] = timePart.split(':').map(Number);
      const mod = modifier.toLowerCase();
      if (mod === 'pm' && h !== 12) h += 12;
      if (mod === 'am' && h === 12) h = 0;
      return h * 60 + (m || 0);
    }

    // Fall back to 24-hour time (e.g., "13:30")
    const twentyFourMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (twentyFourMatch) {
      const h = Number(twentyFourMatch[1]);
      const m = Number(twentyFourMatch[2]);
      return h * 60 + (m || 0);
    }

    return 0;
  };

  const normalizeCategory = (value) => {
    if (value === null || value === undefined) return '';
    return value.toString().trim().toLowerCase().replace(/\s+/g, '_');
  };

  const getEventCategoryKey = (event) => {
    const normalized = normalizeCategory(event.category);
    const tags = Array.isArray(event.tags) ? event.tags.map(t => normalizeCategory(t)) : [];

    if (normalized === 'keynote' || tags.includes('keynote')) return 'keynote';
    if (normalized === 'panel' || tags.includes('panel')) return 'panel';
    if (tags.includes('solo_talk') || tags.includes('solo_talks') || tags.includes('solo_talk')) return 'solo_talk';
    if (tags.includes('fireside_chat') || tags.includes('fireside_chat')) return 'fireside_chat';
    if (normalized === 'engagement_event' || normalized === 'engagement' || tags.includes('engagement')) {
      return 'engagement_event';
    }

    // Map legacy category names
    if (normalized === 'speaker_event') {
      if (tags.includes('solo_talk')) return 'solo_talk';
      if (tags.includes('fireside_chat')) return 'fireside_chat';
      if (tags.includes('panel')) return 'panel';
      return 'solo_talk';
    }

    return normalizeCategory(event.category);
  };

  // Filter first
  const filteredEvents = events.filter(event => {
    const title = (event.title || '').toLowerCase();
    const desc = (event.description || '').toLowerCase();
    const speaker = (event.speaker || '').toLowerCase();
    const tags = Array.isArray(event.tags) ? event.tags : [];
    const matchesSearch =
      title.includes(searchTerm.toLowerCase()) ||
      desc.includes(searchTerm.toLowerCase()) ||
      speaker.includes(searchTerm.toLowerCase()) ||
      tags.some(tag => (tag || '').toLowerCase().includes(searchTerm.toLowerCase()));

    const eventCategoryKey = getEventCategoryKey(event);
    const matchesCategory = selectedCategory === 'all' || eventCategoryKey === selectedCategory;
    const matchesDay = selectedDay === 'all' || event.day === selectedDay;

    return matchesSearch && matchesCategory && matchesDay;
  });

  // Group by day
  const groupedByDay = filteredEvents.reduce((acc, event) => {
    const day = event.day || 'unknown';
    if (!acc[day]) acc[day] = [];
    acc[day].push(event);
    return acc;
  }, {});

  // Sort by time within each day
  Object.keys(groupedByDay).forEach(day => {
    groupedByDay[day].sort((a, b) => {
      const timeA = normalizeTime(a.time || '');
      const timeB = normalizeTime(b.time || '');
      return timeA - timeB;
    });
  });


  const getCategoryColor = (category) => {
    const colors = {
      keynote: 'bg-purple-100 text-purple-800',
      solo_talk: 'bg-blue-100 text-blue-800',
      fireside_chat: 'bg-orange-100 text-orange-800',
      panel: 'bg-green-100 text-green-800',
      engagement_event: 'bg-pink-100 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const handleAddToSchedule = async (event) => {
    const result = await addToSchedule(event);
    setFeedback({
      show: true,
      message: result.message,
      type: result.success ? 'success' : 'error'
    });
    setTimeout(() => setFeedback({ show: false, message: '', type: '' }), 3000);
  };

  if (loading) return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Conference Events</h1>
        <p className="text-gray-600">Explore all the exciting events and sessions at our conference</p>
      </div>

      {feedback.show && (
        <div className={`mb-4 p-4 rounded-lg ${
          feedback.type === 'success'
            ? 'bg-green-100 border border-green-400 text-green-700'
            : 'bg-red-100 border border-red-400 text-red-700'
        }`}>
          {feedback.message}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-8">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search events, speakers, or topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white cursor-pointer"
            >
              {categories.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
            </select>
          </div>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white cursor-pointer"
            >
              {days.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Events by Day */}
      {Object.entries(groupedByDay).map(([day, dayEvents]) => (
        <div key={day} className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {days.find(d => d.value === day)?.label || 'Unscheduled'}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {dayEvents.map(event => {
              const eventId = event.id || event.event_id;
              const isScheduled = isEventScheduled(eventId);
              const tags = Array.isArray(event.tags) ? event.tags : [];
              return (
                <div key={eventId} className="card hover:shadow-lg transition-shadow p-4 md:p-6">
                  <div className="flex justify-between items-start mb-3 md:mb-4">
                    {(() => {
                      const categoryKey = getEventCategoryKey(event);
                      const label = categories.find(c => c.value === categoryKey)?.label || 'Other';
                      return (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(categoryKey)}`}>
                          {label}
                        </span>
                      );
                    })()}
                  </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">{event.title || 'Untitled Event'}</h3>
              <p className="text-gray-600 text-sm mb-4">{event.description || 'Details coming soon.'}</p>

                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    {event.speaker ? (
                      <div className="flex items-center">
                        <Users className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{event.speaker}</span>
                      </div>
                    ) : null}
                    <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-400" />
                  <span>{event.time || 'TBA'}</span>
                </div>
                    <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                  <span>{event.location || 'TBA'}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mb-4">
                {tags.length > 0 ? (
                  tags.map(tag => (
                    <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      {tag}
                    </span>
                  ))
                ) : (
                  <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                    No tags
                  </span>
                )}
              </div>

                  <button
                    onClick={() => handleAddToSchedule(event)}
                    disabled={isScheduled}
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      isScheduled
                        ? 'bg-green-100 text-green-700 cursor-not-allowed flex items-center justify-center'
                        : 'btn-primary'
                    }`}
                  >
                    {isScheduled ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" /> Added to Schedule
                      </>
                    ) : 'Add to Schedule'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {filteredEvents.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <Search className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default Events;
